import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import type { Sale } from '@/services/firestore/types';

export type RevenueClientDetails = {
  id?: string;
  name: string;
  phone?: string;
  email?: string;
  authUid?: string;
};

/** Omit empty/null contact fields so callable payloads pass server Zod validation. */
export function sanitizeRevenueCheckoutClient(client: {
  id?: string | null;
  name: string;
  phone?: string | null;
  email?: string | null;
  authUid?: string | null;
}): RevenueClientDetails {
  const result: RevenueClientDetails = { name: client.name };

  if (client.id) {
    result.id = client.id;
  }
  const phone = client.phone?.trim();
  if (phone) {
    result.phone = phone;
  }
  const email = client.email?.trim();
  if (email) {
    result.email = email;
  }
  if (client.authUid) {
    result.authUid = client.authUid;
  }

  return result;
}

export type RevenueLineItemPayload = {
  type: 'service' | 'product' | 'custom' | 'giftCard' | 'accountCredit';
  description: string;
  quantity: number;
  unitAmountCents?: number;
  taxable: boolean;
  serviceId?: string;
  productId?: string;
  staffId?: string;
  giftCardInitialBalanceCents?: number;
};

export type RevenuePaymentPayload = {
  type: 'cash' | 'card' | 'giftCard' | 'accountBalance' | 'other';
  amountCents: number;
  giftCardId?: string;
  cardOnFileRef?: string;
  note?: string;
};

export type CreateRevenueCheckoutPayload = {
  appointmentId?: string;
  source: 'appointment' | 'walkIn' | 'clientPortal' | 'paymentLink';
  client: RevenueClientDetails;
  lineItems: RevenueLineItemPayload[];
  discounts: Array<{
    label: string;
    type: 'fixed' | 'percent';
    amountCents?: number;
    percentBps?: number;
  }>;
  payments: RevenuePaymentPayload[];
  tipCents: number;
  taxRateBps?: number;
  notes?: string;
  receiptDelivery?: 'platform' | 'stripe';
};

export type CreateRevenuePaymentIntentPayload = {
  saleId?: string;
  appointmentId?: string;
  clientId?: string;
  clientName?: string;
  amountCents: number;
  purpose: 'checkout' | 'cardOnFile' | 'paymentLink';
  setupFutureUsage?: 'off_session';
};

export type CreateRevenueSetupIntentPayload = {
  clientId: string;
};

export type CreateRevenueRefundPayload = {
  saleId: string;
  amountCents: number;
  reason: string;
};

export type VoidCheckoutSalePayload = {
  saleId: string;
};

export type VoidCheckoutSaleResponse = {
  saleId: string;
  status: 'void';
  alreadyVoid?: boolean;
};

export type TerminalReaderMetadata = {
  readerId?: string;
  label?: string;
  deviceType?: string;
  serialNumber?: string;
  tapToPay?: boolean;
};

export type TerminalReaderConfiguration = Omit<TerminalReaderMetadata, 'tapToPay'>;

export type TerminalConfigurationResponse = {
  provider: 'stripe_terminal';
  displayName: string;
  stripeLocationId?: string;
  readers: TerminalReaderConfiguration[];
  tapToPay: {
    enabled: boolean;
    supportedPlatforms: Array<'ios' | 'android'>;
    notes: string;
  };
};

export type TerminalConnectionTokenResponse = {
  secret: string | null;
  stripeLocationId?: string;
};

export type CreateTerminalPaymentIntentPayload = {
  saleId: string;
  amountCents: number;
  reader?: TerminalReaderMetadata;
};

export type TerminalPaymentIntentResponse = {
  paymentIntentId: string;
  clientSecret: string | null;
  status: string;
  captureMethod: 'manual';
  paymentMethodTypes: string[];
  clientEmailOnFile?: string | null;
};

export type UpdateTerminalPaymentReceiptPayload = {
  saleId: string;
  paymentIntentId: string;
  receiptEmail?: string | null;
  clientId?: string;
};

export type UpdateTerminalPaymentReceiptResponse = {
  updated: boolean;
  receiptEmail: string | null;
};

export type CaptureTerminalPaymentPayload = {
  saleId: string;
  paymentIntentId: string;
  reader?: TerminalReaderMetadata;
  expectedBaseAmountCents?: number;
};

export type RecordTerminalPaymentFailurePayload = {
  saleId: string;
  paymentIntentId: string;
  reason: string;
};

export type TerminalPaymentStatusResponse = {
  saleId: string;
  payment: Sale['payments'][number];
  paymentIntentId: string;
  status: Sale['payments'][number]['status'];
  saleStatus?: Sale['status'];
  balanceDueCents?: number;
  tipDeltaCents?: number;
};

export type RevenueCheckoutResponse = {
  saleId: string;
  sale: Sale;
  receipt: Sale['receipt'];
};

export type RevenuePaymentIntentResponse = {
  paymentIntentId: string;
  clientSecret: string | null;
  status: string;
};

export type RevenueSetupIntentResponse = {
  setupIntentId: string;
  clientSecret: string | null;
  cardOnFileRef: {
    id: string;
    provider: 'stripe';
    setupIntentId?: string;
    status: 'setup_pending';
    createdAt: string;
  };
};

export const REVENUE_FUNCTIONS_QUERY_KEY = ['revenueFunctions'];

export async function createRevenueCheckout(payload: CreateRevenueCheckoutPayload) {
  const callable = httpsCallable<CreateRevenueCheckoutPayload, RevenueCheckoutResponse>(
    functions,
    'createRevenueCheckout',
  );
  const result = await callable(payload);

  return result.data;
}

export async function createRevenuePaymentIntent(payload: CreateRevenuePaymentIntentPayload) {
  const callable = httpsCallable<CreateRevenuePaymentIntentPayload, RevenuePaymentIntentResponse>(
    functions,
    'createRevenuePaymentIntent',
  );
  const result = await callable(payload);

  return result.data;
}

export async function createRevenueSetupIntent(payload: CreateRevenueSetupIntentPayload) {
  const callable = httpsCallable<CreateRevenueSetupIntentPayload, RevenueSetupIntentResponse>(
    functions,
    'createRevenueSetupIntent',
  );
  const result = await callable(payload);

  return result.data;
}

export async function createRevenueRefund(payload: CreateRevenueRefundPayload) {
  const callable = httpsCallable<CreateRevenueRefundPayload, { refund: Sale['refunds'][number] }>(
    functions,
    'createRevenueRefund',
  );
  const result = await callable(payload);

  return result.data;
}

export async function voidCheckoutSale(payload: VoidCheckoutSalePayload) {
  const callable = httpsCallable<VoidCheckoutSalePayload, VoidCheckoutSaleResponse>(
    functions,
    'voidCheckoutSale',
  );
  const result = await callable(payload);

  return result.data;
}

export async function getTerminalConfiguration() {
  const callable = httpsCallable<Record<string, never>, TerminalConfigurationResponse>(
    functions,
    'getTerminalConfiguration',
  );
  const result = await callable({});

  return result.data;
}

export async function createTerminalConnectionToken() {
  const callable = httpsCallable<Record<string, never>, TerminalConnectionTokenResponse>(
    functions,
    'createTerminalConnectionToken',
  );
  const result = await callable({});

  return result.data;
}

export async function createTerminalPaymentIntent(payload: CreateTerminalPaymentIntentPayload) {
  const callable = httpsCallable<CreateTerminalPaymentIntentPayload, TerminalPaymentIntentResponse>(
    functions,
    'createTerminalPaymentIntent',
  );
  const result = await callable(payload);

  return result.data;
}

export async function updateTerminalPaymentReceiptEmail(payload: UpdateTerminalPaymentReceiptPayload) {
  const callable = httpsCallable<
    UpdateTerminalPaymentReceiptPayload,
    UpdateTerminalPaymentReceiptResponse
  >(functions, 'updateTerminalPaymentReceiptEmail');
  const result = await callable(payload);

  return result.data;
}

export async function captureTerminalPayment(payload: CaptureTerminalPaymentPayload) {
  const callable = httpsCallable<CaptureTerminalPaymentPayload, TerminalPaymentStatusResponse>(
    functions,
    'captureTerminalPayment',
  );
  const result = await callable(payload);

  return result.data;
}

export async function recordTerminalPaymentFailure(payload: RecordTerminalPaymentFailurePayload) {
  const callable = httpsCallable<RecordTerminalPaymentFailurePayload, TerminalPaymentStatusResponse>(
    functions,
    'recordTerminalPaymentFailure',
  );
  const result = await callable(payload);

  return result.data;
}

